import { generateGuid } from 'utils/Guid';
import { getPortalSetup } from 'utils/PortalSetup';
import { IFileInfo } from '../Upload/FileInfo';
import { ChunkUploader, IFileUploaderCallBack } from 'components/FileLoader/utils/chunkuploader';
import { UiPortalSettings } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal';

export interface IUploadFileOptions {
  file: File;
  callbacks?: IFileUploaderCallBack;
}

export function uploadFile(options: IUploadFileOptions) {
  if (!options.file) return;
  const uploader = new ChunkUploader({ file: options.file, callBacks: options.callbacks });
  uploader.startUpload();
  return uploader;
}

export function fileListToFileArray(files: FileList) {
  const fileArray: File[] = [];
  for (var i = 0; i < files.length; i++) {
    fileArray.push(files[i]);
  }
  return fileArray;
}

export function getFileInfo(files: File[], isMultiFileUpload: boolean) {
  let info: IFileInfo[] = [];
  if (!files || !files.length) return info;
  if (!isMultiFileUpload) files = [files[0]];
  info = files.map(
    file =>
      ({
        fileId: generateGuid(),
        fileName: file.name,
        fileSize: file.size,
        uploaded: false,
        file,
      } as IFileInfo)
  );
  return info;
}

/**Returns an array of valid and invalid files */
export function filterWithMaxSize(maxSize: number, files: File[]): [File[], File[]] {
  let validFiles: File[] = [];
  let invalidFiles: File[] = [];
  const allowedSizeInBytes = kbToBytes(maxSize);
  files.forEach(file => {
    if (file.size > allowedSizeInBytes) invalidFiles.push(file);
    else validFiles.push(file);
  });
  return [validFiles, invalidFiles];
}

/**Returns an array of valid and invalid files */
export function filterWithAllowedExtensions(files: File[], allowedExtensions?: string[]): [File[], File[]] {
  let validFiles: File[] = [];
  let invalidFiles: File[] = [];
  const portalSetup = getPortalSetup();

  const allowedExtensionsAtControl = (allowedExtensions || []).map(e => e.replace('.', '').toLowerCase());
  files.forEach(file => {
    const fileExtension = getFileExtension(file.name).toLowerCase();

    let isValidForControl = portalSetup.Settings.AllowMultipleFileExtensions || file.name.split('.').length <= 2;
    if (isValidForControl) {
      isValidForControl = allowedExtensionsAtControl.length
        ? allowedExtensionsAtControl.indexOf(fileExtension) > -1
        : true;
    }

    const isValidFile = isValidForControl && isFileExtensionValidGlobally(portalSetup.Settings, fileExtension);
    if (isValidFile) validFiles.push(file);
    else invalidFiles.push(file);
  });
  return [validFiles, invalidFiles];
}

function isFileExtensionValidGlobally(uiPortalSettings: UiPortalSettings, fileExtension: string): boolean {
  if (uiPortalSettings.UseExtensionsAllowed === false) {
    const globallyNotAllowedExtensions = (uiPortalSettings.ExtensionsNotAllowed || []).map(e =>
      e.replace('.', '').toLowerCase()
    );
    return globallyNotAllowedExtensions.indexOf(fileExtension) === -1;
  } else if (uiPortalSettings.ExtensionsAllowed && uiPortalSettings.ExtensionsAllowed.length > 0) {
    const globallyAllowedExtensions = (uiPortalSettings.ExtensionsAllowed || []).map(e =>
      e.replace('.', '').toLowerCase()
    );
    return globallyAllowedExtensions.indexOf(fileExtension) > -1;
  }
  return true;
}

export function kbToBytes(size: number) {
  return size * Math.pow(2, 10);
}

export function getFileExtension(fileName: string): string {
  if (fileName && fileName.lastIndexOf('.') > -1) {
    return fileName.substr(fileName.lastIndexOf('.') + 1);
  }
  return '';
}
